import _ from "lodash";
import moment from "moment";
import { DateUtils } from "../utils/DateUtils";

/**
 * Types d'anomalie relevables lors d'une sortie / d'un retour de matériel.
 * Codes alignés sur ceux acceptés par l'API (cf. plan §4.4).
 */
export const ANOMALIE_TYPES: { [code: string]: string } = {
    deterioration: "💥 Détérioration",
    missing: "❌ Pièce manquante",
    dirty: "🧹 État de propreté",
    malfunction: "⚙️ Dysfonctionnement",
    other: "📝 Autre",
};

/** États opérationnels d'un équipement. */
export const ETATS_OPERATIONNELS: { [code: string]: string } = {
    ok: "✓ En service",
    maintenance: "🔧 En maintenance",
    broken: "⛔ Hors service",
};

/** Types d'intervention du carnet d'entretien. */
export const ENTRETIEN_TYPES: Array<string> = [
    "Entretien",
    "Réparation",
    "Révision",
    "Contrôle",
    "Nettoyage",
];

/** Fréquences d'entretien proposées (en jours). */
export const FREQUENCES_ENTRETIEN: Array<number> = [30, 90, 180, 365];

export interface PhotoRef {
    id: number;
    path?: string;
}

export interface MaintenanceStatus {
    code: string;
    label: string;
    days_left?: number | null;
    next_date?: string | null;
}

export interface AnomaliePreexistante {
    id: number;
    date_mouvement?: string;
    anomalie_types?: Array<string>;
    anomalie_description?: string;
    photos_count?: number;
}

export class EntretienMateriel {
    id?: number;
    equipement_id?: number;
    date?: string | null = null;
    type: string = "";
    prestataire: string = "";
    cout?: number | null = null;
    notes: string = "";

    constructor(data?: any) {
        if (data) {
            _.assign(this, data);
        }
    }

    get date_str(): string {
        return DateUtils.sqlToDateStr(this.date);
    }

    serialize(): any {
        return {
            id: this.id,
            equipement_id: this.equipement_id,
            date: this.date,
            type: this.type,
            prestataire: this.prestataire || "",
            cout: this.cout === undefined || this.cout === null ? null : this.cout,
            notes: this.notes || "",
        };
    }
}

export class MouvementMateriel {
    id?: number;
    equipement_id?: number;
    equipement_libelle: string = "";
    equipement_categorie: string = "";
    equipement_emplacement: string = "";
    type: string = "out"; // "out" | "in"
    date_mouvement?: string | null = null;
    heure_mouvement?: string | null = null;
    client_id?: number | null = null;
    client?: any = null;
    destination_libre: string = "";
    destination: string = "";
    employe_id?: number | null = null;
    employe?: any = null;
    employe_nom: string = "";
    responsable_user_id?: number | null = null;
    responsable_nom: string = "";
    notes: string = "";
    signature_employe?: string | null = null;
    signature_responsable?: string | null = null;
    anomalie: boolean = false;
    anomalie_types: Array<string> = [];
    anomalie_description: string = "";
    photos: Array<PhotoRef> = [];
    anomalie_preexistante?: AnomaliePreexistante | null = null;
    groupe_uuid?: string | null = null;
    created_at?: string | null = null;

    constructor(data?: any) {
        if (data) {
            _.assign(this, data);
            if (!this.anomalie_types) {
                this.anomalie_types = [];
            }
            if (!this.photos) {
                this.photos = [];
            }
        }
    }

    /** Date du mouvement au format dd/mm/yyyy. */
    get date_mouvement_str(): string {
        return DateUtils.sqlToDateStr(this.date_mouvement);
    }

    /** Date + heure du mouvement, ex. « 12/03/2026 14:30 ». */
    get date_heure_str(): string {
        let date = this.date_mouvement_str;
        if (!date) {
            return "";
        }
        return this.heure_mouvement ? date + " " + this.heure_mouvement : date;
    }

    get is_sortie(): boolean {
        return this.type === "out";
    }

    get type_label(): string {
        return this.is_sortie ? "Sortie" : "Retour";
    }

    /** Libellés des types d'anomalie du mouvement. */
    get anomalie_types_labels(): Array<string> {
        return _.map(this.anomalie_types || [], function (code) {
            return ANOMALIE_TYPES[code] || code;
        });
    }

    /** Destination affichable (nom du client, sinon destination libre). */
    get destination_str(): string {
        if (this.destination) {
            return this.destination;
        }
        if (this.client && this.client.nom) {
            return this.client.nom;
        }
        return this.destination_libre || "";
    }

    /** Nombre de jours entiers écoulés depuis le mouvement. */
    get jours_dehors(): number {
        return MouvementMateriel.joursDepuis(
            this.date_mouvement,
            this.heure_mouvement
        );
    }

    /**
     * Nombre de jours entiers (plancher) écoulés depuis une date + heure SQL.
     */
    static joursDepuis(date?: string | null, heure?: string | null): number {
        if (!date) {
            return 0;
        }
        let value = String(date).substring(0, 10);
        if (heure) {
            value += " " + String(heure).substring(0, 5);
        }
        let ref = moment(
            value,
            heure ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD"
        );
        if (!ref.isValid()) {
            return 0;
        }
        let diff = moment().diff(ref, "days");
        return diff > 0 ? diff : 0;
    }

    serialize(): any {
        return {
            id: this.id,
            equipement_id: this.equipement_id,
            type: this.type,
            date_mouvement: this.date_mouvement,
            heure_mouvement: this.heure_mouvement,
            client_id: this.client_id || null,
            destination_libre: this.destination_libre || "",
            employe_id: this.employe_id || null,
            notes: this.notes || "",
            signature_employe: this.signature_employe || null,
            signature_responsable: this.signature_responsable || null,
            anomalie: !!this.anomalie,
            anomalie_types: this.anomalie_types || [],
            anomalie_description: this.anomalie_description || "",
        };
    }
}

export class EquipementMateriel {
    id?: number;
    categorie_id?: number | null = null;
    categorie_nom: string = "";
    numero?: number | null = null;
    libelle: string = "";
    emplacement_id?: number | null = null;
    emplacement_nom: string = "";
    modele: string = "";
    numero_serie: string = "";
    code_barre: string = "";
    photo_id?: number | null = null;
    photo?: PhotoRef | null = null;
    notes: string = "";
    etat_operationnel: string = "ok";
    date_achat?: string | null = null;
    date_mise_en_service?: string | null = null;
    fin_garantie?: string | null = null;
    frequence_entretien_jours?: number | null = null;
    archived: boolean = false;
    archived_at?: string | null = null;
    statut: string = "disponible"; // "disponible" | "sorti"
    maintenance?: MaintenanceStatus | null = null;
    dernier_mouvement?: MouvementMateriel | null = null;
    entretiens_count: number = 0;
    cout_entretiens_total: number = 0;

    constructor(data?: any) {
        if (data) {
            _.assign(this, _.omit(data, ["dernier_mouvement"]));
            if (data.dernier_mouvement) {
                this.dernier_mouvement = new MouvementMateriel(
                    data.dernier_mouvement
                );
            }
        }
    }

    get is_sorti(): boolean {
        return this.statut === "sorti";
    }

    get etat_label(): string {
        return ETATS_OPERATIONNELS[this.etat_operationnel] || "";
    }

    get date_achat_str(): string {
        return DateUtils.sqlToDateStr(this.date_achat);
    }

    get date_mise_en_service_str(): string {
        return DateUtils.sqlToDateStr(this.date_mise_en_service);
    }

    get fin_garantie_str(): string {
        return DateUtils.sqlToDateStr(this.fin_garantie);
    }

    get archived_at_str(): string {
        return DateUtils.sqlToDateStr(this.archived_at);
    }

    /** Prochaine date d'entretien au format dd/mm/yyyy. */
    get maintenance_next_date_str(): string {
        return this.maintenance && this.maintenance.next_date
            ? DateUtils.sqlToDateStr(this.maintenance.next_date)
            : "";
    }

    /** Nombre de jours entiers depuis la sortie en cours (0 si disponible). */
    get jours_dehors(): number {
        if (!this.is_sorti || !this.dernier_mouvement) {
            return 0;
        }
        return this.dernier_mouvement.jours_dehors;
    }

    serialize(): any {
        return {
            id: this.id,
            categorie_id: this.categorie_id || null,
            numero: this.numero || null,
            emplacement_id: this.emplacement_id || null,
            modele: this.modele || "",
            numero_serie: this.numero_serie || "",
            // null (et non "") : la colonne porte un index unique.
            code_barre: this.code_barre || null,
            photo_id: this.photo_id || null,
            notes: this.notes || "",
            etat_operationnel: this.etat_operationnel || "ok",
            date_achat: this.date_achat || null,
            date_mise_en_service: this.date_mise_en_service || null,
            fin_garantie: this.fin_garantie || null,
            frequence_entretien_jours: this.frequence_entretien_jours || null,
        };
    }
}

/** Groupe renvoyé par `materiel/historique` (un équipement + ses mouvements). */
export interface HistoriqueGroupe {
    equipement: EquipementMateriel;
    nb_sorties: number;
    nb_retours: number;
    nb_anomalies: number;
    mouvements: Array<MouvementMateriel>;
}
